'use client';

import { useRouter } from 'next/navigation';
import React from 'react';

import { Button } from './Button';
import { LogoArbitralis } from './LogoArbitralis';

type ErrorStateProps = {
  title?: string;
  description?: string;
  /** Resets the error boundary and attempts to re-render the segment. */
  onReset?: () => void;
  /** When true, renders without router-dependent actions (for global-error). */
  standalone?: boolean;
  homeHref?: string;
  className?: string;
  retryButtonText?: string;
  homeButtonText?: string;
};

export function ErrorState({
  title = 'Algo deu errado',
  description = 'Encontramos um problema ao carregar esta página. Você pode tentar novamente ou voltar para continuar sua navegação.',
  onReset,
  standalone = false,
  homeHref = '/',
  className,
  retryButtonText,
  homeButtonText,
}: ErrorStateProps) {
  const router = useRouter();

  const handleHome = () => {
    if (standalone) {
      window.location.assign(homeHref);
      return;
    }

    router.push(homeHref);
  };

  return (
    <div
      className={`bg-default-background fixed inset-0 flex h-screen w-screen items-center justify-center px-6 py-10 ${
        className ?? ''
      }`}
    >
      <div className="relative w-full max-w-[720px] overflow-hidden rounded-[20px] border border-solid border-neutral-200 bg-white p-8 shadow-sm">
        <div className="bg-error-100 pointer-events-none absolute -top-16 -right-16 h-56 w-56 animate-pulse rounded-full opacity-50 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-24 -left-24 h-64 w-64 animate-pulse rounded-full bg-neutral-100 opacity-80 blur-3xl" />

        <div className="relative flex w-full flex-col items-center gap-6">
          <div className="flex flex-col items-center gap-3">
            <LogoArbitralis className="opacity-90" />
            <div className="relative flex items-center justify-center">
              <div className="bg-error-50 absolute inset-0 animate-pulse rounded-full opacity-70 blur-xl" />
              <Illustration />
            </div>
          </div>

          <div className="flex w-full flex-col items-center gap-2">
            <span className="text-heading-2 font-heading-2 text-default-font text-center">
              {title}
            </span>
            <span className="text-body font-body text-subtext-color text-center">
              {description}
            </span>
          </div>

          <div className="flex items-center gap-3">
            {onReset ? (
              <Button variant="brand-primary" onClick={onReset}>
                {retryButtonText || 'Tentar novamente'}
              </Button>
            ) : null}
            <Button variant="neutral-secondary" onClick={handleHome}>
              {homeButtonText || 'Ir para o início'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Illustration() {
  return (
    <svg
      width="220"
      height="140"
      viewBox="0 0 220 140"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="relative"
    >
      <rect x="28" y="18" width="116" height="92" rx="14" className="fill-neutral-50" />
      <rect x="40" y="34" width="92" height="10" rx="5" className="fill-neutral-200" />
      <rect x="40" y="52" width="72" height="10" rx="5" className="fill-neutral-200" />
      <rect x="40" y="70" width="84" height="10" rx="5" className="fill-neutral-200" />
      <rect x="40" y="88" width="56" height="10" rx="5" className="fill-neutral-200" />

      <circle cx="156" cy="78" r="28" className="fill-error-100" />
      <circle cx="156" cy="78" r="18" className="fill-white" />
      <circle cx="156" cy="78" r="19" className="fill-error-200 opacity-50" />

      <path d="M156 67v13" className="stroke-error-500" strokeWidth="4" strokeLinecap="round" />
      <circle cx="156" cy="89" r="2.5" className="fill-error-500" />

      <circle cx="10" cy="70" r="6" className="fill-error-200 animate-bounce opacity-70" />
      <circle cx="199" cy="90" r="5" className="fill-error-200 animate-bounce opacity-50" />
      <circle cx="110" cy="16" r="6" className="animate-pulse fill-neutral-300 opacity-80" />
      <circle cx="80" cy="16" r="8" className="animate-pulse fill-neutral-300 opacity-80" />
      <circle cx="50" cy="16" r="10" className="animate-pulse fill-neutral-300 opacity-80" />
    </svg>
  );
}
